<template>
  <div class="reimbursement-add-page">
    <!-- Page intro -->
    <b-card no-body class="rr-intro-card mb-1">
      <b-card-body class="py-1 px-1 px-md-2">
        <div class="d-flex align-items-start flex-wrap">
          <div class="rr-intro-icon mr-1">
            <feather-icon icon="CreditCardIcon" size="20" />
          </div>
          <div class="flex-grow-1">
            <h5 class="rr-intro-title mb-25">Submit a new reimbursement claim</h5>
            <p class="rr-intro-text mb-0">
              Fill in the expense details below. Claims go through
              <strong>{{ approvalFlowLabel }}</strong> approval before payment.
              Fields marked with <span class="text-danger">*</span> are required.
            </p>
          </div>
          <router-link
            :to="{ name: 'apps-reimbursement' }"
            class="rr-back-link mt-50 mt-md-0"
          >
            <feather-icon icon="ArrowLeftIcon" size="14" class="mr-25" />
            Back to list
          </router-link>
        </div>
      </b-card-body>
    </b-card>

    <b-row class="rr-layout-row">
      <!-- Main form -->
      <b-col cols="12" lg="8" xl="9">
        <validation-observer ref="reimbursementRules">
          <b-form @submit.prevent>
            <!-- Section 1: What & Why -->
            <b-card no-body class="rr-section-card mb-1">
              <b-card-body class="p-1 p-md-2">
                <div class="rr-section-header">
                  <span class="rr-section-icon variant-primary">
                    <feather-icon icon="FileTextIcon" size="13" />
                  </span>
                  <span class="rr-section-title">What &amp; Why</span>
                  <div class="flex-grow-1 ml-2 rr-section-rule" />
                </div>
                <b-row>
                  <b-col v-if="canApplyOnBehalf" cols="12" md="6">
                    <b-form-group label-for="apply-on-behalf">
                      <template #label>Apply on behalf of</template>
                      <v-select
                        id="apply-on-behalf"
                        v-model="appliedForEmployee"
                        :options="onBehalfEmployeeOptions"
                        label="title"
                        placeholder="Myself (default)"
                        :loading="onBehalfLoading"
                        :clearable="false"
                        @open="ensureOnBehalfEmployees"
                        @input="onAppliedForChange"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6">
                    <b-form-group label-for="item-name">
                      <template #label>
                        Item / Title
                        <span v-if="isFieldRequired('itemName')" class="text-danger">*</span>
                      </template>
                      <validation-provider
                        #default="{ errors }"
                        name="Item / Title"
                        :rules="isFieldRequired('itemName') ? 'required' : ''"
                      >
                        <b-form-input
                          id="item-name"
                          v-model="reimbursement.itemName"
                          placeholder="e.g. Office supplies, Software license"
                          :state="itemTitleError || errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ itemTitleError || errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12">
                    <b-form-group label-for="reimbursement-comment">
                      <template #label>
                        Description / Comment
                        <span v-if="isFieldRequired('comment')" class="text-danger">*</span>
                      </template>
                      <validation-provider
                        #default="{ errors }"
                        name="Description / Comment"
                        :rules="isFieldRequired('comment') ? 'required' : ''"
                      >
                        <b-form-textarea
                          id="reimbursement-comment"
                          v-model="reimbursement.comment"
                          placeholder="Describe what this reimbursement is for — include purpose, vendor, or project context"
                          rows="3"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <!-- Section 2: Classification -->
            <b-card no-body class="rr-section-card mb-1">
              <b-card-body class="p-1 p-md-2">
                <div class="rr-section-header">
                  <span class="rr-section-icon variant-primary">
                    <feather-icon icon="TagIcon" size="13" />
                  </span>
                  <span class="rr-section-title">Classification</span>
                  <div class="flex-grow-1 ml-2 rr-section-rule" />
                </div>
                <b-row>
                  <b-col cols="12" md="6">
                    <b-form-group label-for="account-name">
                      <template #label>
                        Account / Client
                        <span v-if="isFieldRequired('accountName')" class="text-danger">*</span>
                      </template>
                      <validation-provider
                        #default="{ errors }"
                        name="Account / Client"
                        :rules="isFieldRequired('accountName') ? 'required' : ''"
                      >
                        <v-select
                          id="account-name"
                          v-model="reimbursement.accountName"
                          :options="accountsList"
                          label="title"
                          :reduce="(option) => option.title"
                          :state="errors.length > 0 ? false : null"
                          placeholder="Select Account / Client"
                          :loading="configLoading.clients"
                          @open="ensureClients"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6">
                    <b-form-group label-for="vertical">
                      <template #label>
                        Vertical
                        <span v-if="isFieldRequired('vertical')" class="text-danger">*</span>
                      </template>
                      <validation-provider
                        #default="{ errors }"
                        name="Vertical"
                        :rules="isFieldRequired('vertical') ? 'required' : ''"
                      >
                        <v-select
                          id="vertical"
                          v-model="reimbursement.vertical"
                          :options="verticalList"
                          label="title"
                          :reduce="(option) => option.title"
                          :state="errors.length > 0 ? false : null"
                          placeholder="Select Vertical"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6">
                    <b-form-group label-for="category">
                      <template #label>
                        Category
                        <span v-if="isFieldRequired('category')" class="text-danger">*</span>
                      </template>
                      <validation-provider
                        #default="{ errors }"
                        name="Category"
                        :rules="isFieldRequired('category') ? 'required' : ''"
                      >
                        <v-select
                          id="category"
                          v-model="reimbursement.category"
                          :options="categoryList"
                          label="title"
                          :reduce="(option) => option.title"
                          :state="errors.length > 0 ? false : null"
                          placeholder="Select Category"
                          :loading="configLoading.categories"
                          @open="ensureCategories"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" v-if="isSubCategoryVisible">
                    <b-form-group label-for="sub-category">
                      <template #label>
                        Sub-Category
                        <span v-if="isSubCategoryRequired" class="text-danger">*</span>
                      </template>
                      <validation-provider
                        #default="{ errors }"
                        name="Sub-Category"
                        :rules="isSubCategoryRequired ? 'required' : ''"
                      >
                        <v-select
                          id="sub-category"
                          v-model="reimbursement.subCategory"
                          :options="filteredSubCategories"
                          label="title"
                          :reduce="(option) => option.title"
                          :state="errors.length > 0 ? false : null"
                          placeholder="Select Sub-Category"
                          @open="ensureCategories"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6">
                    <b-form-group label-for="location">
                      <template #label>
                        Location
                        <span v-if="isFieldRequired('location')" class="text-danger">*</span>
                      </template>
                      <validation-provider
                        #default="{ errors }"
                        name="Location"
                        :rules="isFieldRequired('location') ? 'required' : ''"
                      >
                        <v-select
                          id="location"
                          v-model="reimbursement.location"
                          :options="locationList"
                          label="title"
                          :reduce="(option) => option.title"
                          :state="errors.length > 0 ? false : null"
                          placeholder="Select Location"
                          :loading="configLoading.locations"
                          @open="ensureLocations"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <!-- Section 3: Amount & Payment -->
            <b-card no-body class="rr-section-card mb-1">
              <b-card-body class="p-1 p-md-2">
                <div class="rr-section-header">
                  <span class="rr-section-icon variant-primary">
                    <feather-icon icon="DollarSignIcon" size="13" />
                  </span>
                  <span class="rr-section-title">Amount &amp; Payment</span>
                  <div class="flex-grow-1 ml-2 rr-section-rule" />
                </div>
                <b-row>
                  <b-col cols="12" md="4">
                    <b-form-group label-for="amount">
                      <template #label>
                        Amount (₹)
                        <span v-if="isFieldRequired('amount')" class="text-danger">*</span>
                      </template>
                      <validation-provider
                        #default="{ errors }"
                        name="Amount"
                        :rules="isFieldRequired('amount') ? 'required|double' : 'double'"
                      >
                        <b-input-group>
                          <b-input-group-prepend is-text>₹</b-input-group-prepend>
                          <b-form-input
                            id="amount"
                            v-model="reimbursement.amount"
                            :state="errors.length > 0 ? false : null"
                            placeholder="0.00"
                            type="number"
                            step=".01"
                            min="0"
                          />
                        </b-input-group>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="4">
                    <b-form-group label-for="payment-mode">
                      <template #label>
                        Payment Mode
                        <span v-if="isFieldRequired('paymentMode')" class="text-danger">*</span>
                      </template>
                      <validation-provider
                        #default="{ errors }"
                        name="Payment Mode"
                        :rules="isFieldRequired('paymentMode') ? 'required' : ''"
                      >
                        <v-select
                          id="payment-mode"
                          v-model="reimbursement.paymentMode"
                          :options="paymentModeList"
                          label="title"
                          :reduce="(option) => option.title"
                          :state="errors.length > 0 ? false : null"
                          placeholder="Select Payment Mode"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="4">
                    <b-form-group label-for="expense-date">
                      <template #label>
                        Expense Date
                        <span v-if="isFieldRequired('expenseDate')" class="text-danger">*</span>
                      </template>
                      <validation-provider
                        #default="{ errors }"
                        name="Date"
                        :rules="isFieldRequired('expenseDate') ? 'required' : ''"
                      >
                        <flat-pickr
                          id="expense-date"
                          v-model="reimbursement.expenseDate"
                          class="form-control"
                          :class="{ 'is-invalid': errors.length > 0 }"
                          placeholder="YYYY-MM-DD"
                          :config="{ enableTime: false, dateFormat: 'Y-m-d', maxDate: 'today' }"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6">
                    <b-form-group label-for="invoice-no">
                      <template #label>
                        Invoice No
                        <small class="text-muted font-weight-normal">(optional)</small>
                      </template>
                      <b-form-input
                        id="invoice-no"
                        v-model="reimbursement.invoiceNo"
                        placeholder="Vendor invoice number, if any"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <!-- Section 4: Tax & Compliance -->
            <b-card no-body class="rr-section-card mb-1">
              <b-card-body class="p-1 p-md-2">
                <div class="rr-section-header">
                  <span class="rr-section-icon variant-muted">
                    <feather-icon icon="PercentIcon" size="13" />
                  </span>
                  <span class="rr-section-title">Tax &amp; Compliance</span>
                  <span class="text-muted small ml-2">Optional — fill if applicable</span>
                  <div class="flex-grow-1 ml-2 rr-section-rule" />
                </div>
                <b-row>
                  <b-col cols="12" md="4">
                    <b-form-group label-for="is-gst">
                      <template #label>GST Invoice?</template>
                      <v-select
                        id="is-gst"
                        v-model="reimbursement.isGSTInvoice"
                        :options="booleanList"
                        label="title"
                        :reduce="(option) => option.value"
                        placeholder="Select Yes or No"
                        :clearable="false"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="8" v-if="reimbursement.isGSTInvoice">
                    <b-form-group label-for="gst-number">
                      <template #label>
                        GST Number
                        <span class="text-danger">*</span>
                      </template>
                      <validation-provider
                        #default="{ errors }"
                        name="GST Number"
                        rules="required"
                      >
                        <v-select
                          id="gst-number"
                          v-model="reimbursement.gstNumber"
                          :options="gstNumberList"
                          label="title"
                          :reduce="(option) => option.title"
                          :state="errors.length > 0 ? false : null"
                          placeholder="Select company GSTIN"
                          :loading="configLoading.gst"
                          @open="ensureGstNumbers"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="4" v-if="showTDSPrompt">
                    <b-form-group label-for="is-tds">
                      <template #label>TDS Applicable?</template>
                      <v-select
                        id="is-tds"
                        v-model="reimbursement.isTDSDeduction"
                        :options="booleanList"
                        label="title"
                        :reduce="(option) => option.value"
                        placeholder="Select Yes or No"
                        :clearable="false"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="4" v-if="showTDSPrompt && reimbursement.isTDSDeduction">
                    <b-form-group label-for="tds-amount">
                      <template #label>
                        TDS Amount (₹)
                        <span class="text-danger">*</span>
                      </template>
                      <validation-provider
                        #default="{ errors }"
                        name="TDS Amount"
                        rules="required|double"
                      >
                        <b-input-group>
                          <b-input-group-prepend is-text>₹</b-input-group-prepend>
                          <b-form-input
                            id="tds-amount"
                            v-model="reimbursement.tdsAmount"
                            :state="errors.length > 0 ? false : null"
                            placeholder="0.00"
                            type="number"
                            step=".01"
                            min="0"
                          />
                        </b-input-group>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <!-- Section 5: Attachments -->
            <b-card no-body class="rr-section-card mb-1">
              <b-card-body class="p-1 p-md-2">
                <div class="rr-section-header">
                  <span class="rr-section-icon variant-muted">
                    <feather-icon icon="PaperclipIcon" size="13" />
                  </span>
                  <span class="rr-section-title">Supporting Documents</span>
                  <span class="text-danger small ml-50">*</span>
                  <div class="flex-grow-1 ml-2 rr-section-rule" />
                </div>
                <p class="rr-upload-hint mb-1">
                  Attach receipt, invoice, or proof of payment (required).
                  PDF, Excel, Word, or image — max {{ REIMBURSEMENT_MAX_FILE_SIZE_LABEL }}.
                </p>

                <!-- Selected file preview -->
                <div v-if="file" class="rr-file-preview mb-1">
                  <div class="rr-file-preview-icon">
                    <feather-icon :icon="fileIcon" size="20" />
                  </div>
                  <div class="rr-file-preview-meta flex-grow-1">
                    <div class="rr-file-preview-name">{{ file.name }}</div>
                    <div class="rr-file-preview-size">{{ formattedFileSize }}</div>
                  </div>
                  <b-button
                    variant="flat-danger"
                    size="sm"
                    class="btn-icon rr-file-remove-btn"
                    v-b-tooltip.hover="'Remove file'"
                    @click="clearFile"
                  >
                    <feather-icon icon="XIcon" size="16" />
                  </b-button>
                </div>

                <!-- Upload drop zone -->
                <div
                  class="rr-upload-zone"
                  :class="{
                    'rr-upload-zone--has-file': !!file,
                    'rr-upload-zone--error': !!fileSizeError || attachmentRequiredError,
                    'rr-upload-zone--dragover': dragOver,
                  }"
                  @click="triggerFileInput"
                  @dragover.prevent="dragOver = true"
                  @dragleave.prevent="dragOver = false"
                  @drop.prevent="onFileDrop"
                >
                  <input
                    ref="fileInput"
                    type="file"
                    class="d-none"
                    accept="application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/pdf, text/plain, application/rtf, text/markdown, image/*, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel,.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    @change="onFileSelected"
                  />
                  <feather-icon icon="UploadCloudIcon" size="28" class="rr-upload-zone-icon" />
                  <p class="rr-upload-zone-title mb-25">
                    {{ file ? 'Replace file' : 'Drag & drop or click to browse' }}
                  </p>
                  <p class="rr-upload-zone-sub text-muted mb-0">
                    PDF, DOC, XLS, JPG, PNG
                  </p>
                </div>
                <small v-if="fileSizeError" class="text-danger d-block mt-50">{{ fileSizeError }}</small>
                <small v-else-if="attachmentRequiredError" class="text-danger d-block mt-50">{{ attachmentRequiredError }}</small>
              </b-card-body>
            </b-card>

            <!-- Mobile-only summary (hidden on lg+) -->
            <b-card no-body class="rr-summary-card rr-summary-card--mobile mb-1 d-lg-none">
              <b-card-body class="p-1">
                <div class="rr-section-header mb-75">
                  <span class="rr-section-icon variant-accent">
                    <feather-icon icon="CheckSquareIcon" size="13" />
                  </span>
                  <span class="rr-section-title">Claim Summary</span>
                </div>
                <claim-summary-list :summary="reviewSummary" :amount-display="amountDisplay" />
              </b-card-body>
            </b-card>

            <!-- Error alert -->
            <b-alert :show="!!submitError" variant="danger" class="rr-error-alert">
              <div class="d-flex align-items-start">
                <feather-icon icon="AlertCircleIcon" size="18" class="mr-50 flex-shrink-0 mt-25" />
                <span>{{ submitError }}</span>
              </div>
            </b-alert>

            <!-- Form actions (desktop inline) -->
            <div class="rr-form-actions d-none d-lg-flex">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                variant="primary"
                class="rr-submit-btn"
                :disabled="submitting"
                @click="openReviewModal"
              >
                <b-spinner v-if="submitting" small class="mr-50" />
                <feather-icon v-else icon="SendIcon" size="15" class="mr-50" />
                {{ submitting ? 'Submitting…' : 'Review & Submit' }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="reset"
                variant="outline-secondary"
                :disabled="submitting"
                @click="reset"
              >
                <feather-icon icon="RotateCcwIcon" size="15" class="mr-50" />
                Reset
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-col>

      <!-- Sticky sidebar summary (desktop) -->
      <b-col cols="12" lg="4" xl="3" class="d-none d-lg-block">
        <div class="rr-sidebar-sticky">
          <b-card no-body class="rr-summary-card">
            <b-card-body class="p-1 p-md-2">
              <div class="rr-section-header mb-1">
                <span class="rr-section-icon variant-accent">
                  <feather-icon icon="CheckSquareIcon" size="13" />
                </span>
                <span class="rr-section-title">Claim Summary</span>
              </div>

              <!-- Completion progress -->
              <div class="rr-progress-block mb-1">
                <div class="d-flex justify-content-between align-items-center mb-25">
                  <span class="rr-summary-meta-label">Form completion</span>
                  <span class="rr-summary-meta-value font-weight-bold" :class="formCompletionPercent >= 100 ? 'text-success' : 'text-primary'">
                    {{ formCompletionPercent }}%
                  </span>
                </div>
                <b-progress
                  :value="formCompletionPercent"
                  :max="100"
                  height="6px"
                  :variant="formCompletionPercent >= 100 ? 'success' : 'primary'"
                />
              </div>

              <!-- Amount highlight -->
              <div class="rr-amount-highlight mb-1">
                <span class="rr-summary-meta-label d-block">Claim amount</span>
                <span class="rr-amount-value">{{ amountDisplay }}</span>
              </div>

              <claim-summary-list :summary="reviewSummary" :amount-display="amountDisplay" />

              <!-- Approval flow hint -->
              <div class="rr-approval-hint mt-1">
                <feather-icon icon="GitBranchIcon" size="16" class="mr-25 text-primary" />
                <span class="rr-approval-hint-text">
                  After submit: <strong>{{ firstStagePendingLabel }}</strong> → {{ laterStageFlowLabel }} approval
                </span>
              </div>
            </b-card-body>
          </b-card>
        </div>
      </b-col>
    </b-row>

    <!-- Sticky mobile action bar -->
    <div class="rr-mobile-action-bar d-lg-none">
      <b-button
        variant="outline-secondary"
        class="rr-mobile-reset-btn"
        :disabled="submitting"
        @click="reset"
      >
        Reset
      </b-button>
      <b-button
        variant="primary"
        class="rr-mobile-submit-btn"
        :disabled="submitting"
        @click="openReviewModal"
      >
        <b-spinner v-if="submitting" small class="mr-50" />
        <feather-icon v-else icon="SendIcon" size="15" class="mr-50" />
        {{ submitting ? 'Submitting…' : 'Review & Submit' }}
      </b-button>
    </div>

    <!-- Review & confirm modal -->
    <b-modal
      id="rr-review-modal"
      v-model="showReviewModal"
      title="Review your claim"
      size="md"
      ok-title="Submit claim"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      :ok-disabled="submitting"
      no-close-on-backdrop
      @ok.prevent="confirmSubmit"
    >
      <p class="text-muted small mb-1">
        Please verify the details below before submitting. You can go back to edit if anything looks wrong.
      </p>
      <div class="rr-review-amount mb-1">
        <small class="text-muted d-block">Total claim amount</small>
        <span class="rr-amount-value">{{ amountDisplay }}</span>
      </div>
      <claim-summary-list :summary="reviewSummary" :amount-display="amountDisplay" dense />
      <b-alert v-if="file" show variant="light" class="mt-1 mb-0 py-75">
        <feather-icon icon="PaperclipIcon" size="14" class="mr-50" />
        <strong>Attachment:</strong> {{ file.name }} ({{ formattedFileSize }})
      </b-alert>
    </b-modal>
  </div>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BCard,
  BCardBody,
  BRow,
  BCol,
  BFormTextarea,
  BButton,
  BAlert,
  BInputGroup,
  BInputGroupPrepend,
  BProgress,
  BSpinner,
  BModal,
  VBTooltip,
} from 'bootstrap-vue'

import { ValidationProvider, ValidationObserver } from 'vee-validate'
import clientService from '@/services/client-service'
import locationService from '@/services/location-service'
import AdminConfigService from '@/services/admin-config-service'
import invoiceConfigService from '@/services/invoice-config-service'
import userService from '@/services/user-service'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import { required, double } from '@validations'
import reimbursementService, { formatReimbursementApiError } from '@/services/reimbursement-service'
import {
  canApplyReimbursementOnBehalf,
  canApplyReimbursementOnBehalfAll,
  toAddedBySnapshot,
  getReimbursementApprovalFlowLabel,
  getReimbursementStageName,
  formatReimbursementStageOutcome,
  rememberReimbursementOnBehalf,
  REIMBURSEMENT_STATUSES,
} from '@/utils/reimbursement-permissions'
import {
  isFieldRequired,
  resolveOptions,
  getSubCategoryOptions,
  extractExpenseSubCategoryRows,
  categoryRequiresTDSPrompt,
  USE_LIVE_PAYMENT_MODE_LIST,
  PAYMENT_MODE_OPTIONS,
  USE_LIVE_CLIENT_LIST,
  ACCOUNT_NAME_OPTIONS,
  USE_LIVE_LOCATION_LIST,
  LOCATION_OPTIONS,
  USE_LIVE_CATEGORY_LIST,
  CATEGORY_OPTIONS,
  VERTICAL_OPTIONS,
  USE_LIVE_GST_NUMBER_LIST,
  GST_NUMBER_OPTIONS,
  SEND_BUSINESS_UNIT,
  BUSINESS_UNIT_DEFAULT,
  SEND_GST_AMOUNT,
  REIMBURSEMENT_FILE_FIELD,
  REIMBURSEMENT_MAX_FILE_SIZE_LABEL,
  getReimbursementFileSizeError,
  getReimbursementAttachmentRequiredError,
  getItemTitleRequiredError,
  REQUIRED_FIELDS,
} from '@/utils/reimbursement-config'

/** Inline summary list — keeps template DRY across sidebar, mobile, and modal */
const ClaimSummaryList = {
  name: 'ClaimSummaryList',
  functional: true,
  props: {
    summary: { type: Array, required: true },
    amountDisplay: { type: String, default: '—' },
    dense: { type: Boolean, default: false },
  },
  render(h, { props }) {
    const rows = props.summary.filter(row => row.value && row.value !== '—')
    if (rows.length === 0) {
      return h('p', { class: 'rr-summary-empty text-muted mb-0' }, 'Fill in the form to see a summary here.')
    }
    return h(
      'ul',
      { class: ['rr-summary-list', props.dense ? 'rr-summary-list--dense' : ''] },
      rows.map(row =>
        h('li', { key: row.key, class: 'rr-summary-item' }, [
          h('span', { class: 'rr-summary-label' }, row.label),
          h('span', { class: 'rr-summary-value' }, row.value),
        ]),
      ),
    )
  },
}

export default {
  components: {
    BButton,
    BForm,
    BFormGroup,
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormTextarea,
    BFormInput,
    BAlert,
    BInputGroup,
    BInputGroupPrepend,
    BProgress,
    BSpinner,
    BModal,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    flatPickr,
    ClaimSummaryList,
  },
  data() {
    return {
      REIMBURSEMENT_MAX_FILE_SIZE_LABEL,
      reimbursement: {
        itemName: null,
        accountName: null,
        vertical: null,
        invoiceNo: null,
        category: null,
        subCategory: null,
        expenseDate: null,
        paymentMode: null,
        isGSTInvoice: false,
        isTDSDeduction: false,
        amount: null,
        gstNumber: null,
        tdsAmount: null,
        location: null,
        comment: null,
        addedBy: null,
      },
      liveAccountsList: [],
      liveCategoryList: [],
      liveSubCategoryList: [],
      liveLocationList: [],
      livePaymentModeList: [],
      liveGstNumberList: [],
      booleanList: [
        { title: 'YES', value: true },
        { title: 'NO', value: false },
      ],
      file: null,
      submitting: false,
      submitError: null,
      showReviewModal: false,
      dragOver: false,
      attachmentValidationAttempted: false,
      itemTitleValidationAttempted: false,
      appliedForEmployee: null,
      appliedForSnapshot: null,
      onBehalfEmployees: [],
      onBehalfLoaded: false,
      onBehalfLoading: false,
      selfAddedBy: null,
      configLoaded: {
        clients: false,
        categories: false,
        locations: false,
        gst: false,
      },
      configLoading: {
        clients: false,
        categories: false,
        locations: false,
        gst: false,
      },
    }
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  computed: {
    approvalFlowLabel() {
      return getReimbursementApprovalFlowLabel()
    },
    firstStagePendingLabel() {
      return formatReimbursementStageOutcome(REIMBURSEMENT_STATUSES.PENDING_L1, 'pending')
    },
    laterStageFlowLabel() {
      return [
        getReimbursementStageName(REIMBURSEMENT_STATUSES.PENDING_L2),
        getReimbursementStageName(REIMBURSEMENT_STATUSES.PENDING_FINAL),
      ].filter(Boolean).join(' → ')
    },
    accountsList() {
      return resolveOptions(USE_LIVE_CLIENT_LIST, this.liveAccountsList, ACCOUNT_NAME_OPTIONS.map(title => ({ title })))
    },
    verticalList() {
      return VERTICAL_OPTIONS.map(title => ({ title }))
    },
    categoryList() {
      return resolveOptions(
        USE_LIVE_CATEGORY_LIST,
        this.liveCategoryList,
        CATEGORY_OPTIONS.map(title => ({ title })),
      )
    },
    locationList() {
      return resolveOptions(USE_LIVE_LOCATION_LIST, this.liveLocationList, LOCATION_OPTIONS.map(title => ({ title })))
    },
    paymentModeList() {
      return resolveOptions(USE_LIVE_PAYMENT_MODE_LIST, this.livePaymentModeList, PAYMENT_MODE_OPTIONS.map(title => ({ title })))
    },
    gstNumberList() {
      return resolveOptions(
        USE_LIVE_GST_NUMBER_LIST,
        this.liveGstNumberList,
        GST_NUMBER_OPTIONS.map(title => ({ title })),
      )
    },
    filteredSubCategories() {
      return getSubCategoryOptions(this.reimbursement.category, this.liveSubCategoryList).map(title => ({ title }))
    },
    isSubCategoryVisible() {
      return !!this.reimbursement.category && this.filteredSubCategories.length > 0
    },
    isSubCategoryRequired() {
      return isFieldRequired('subCategory') && this.isSubCategoryVisible
    },
    showTDSPrompt() {
      return categoryRequiresTDSPrompt(this.reimbursement.category, this.reimbursement.subCategory)
    },
    fileSizeError() {
      return getReimbursementFileSizeError(this.file)
    },
    canApplyOnBehalf() {
      return canApplyReimbursementOnBehalf(this.$can.bind(this))
    },
    onBehalfEmployeeOptions() {
      const others = this.onBehalfEmployees
        .map(emp => {
          const snapshot = toAddedBySnapshot(emp)
          if (!snapshot || snapshot.empId == null) return null
          if (this.empId != null && String(snapshot.empId) === String(this.empId)) return null
          return { title: snapshot.title, empId: snapshot.empId, email: snapshot.email }
        })
        .filter(Boolean)
      return [
        { title: 'Myself (default)', empId: null, email: (this.selfAddedBy && this.selfAddedBy.email) || null },
        ...others,
      ]
    },
    itemTitleError() {
      if (!this.itemTitleValidationAttempted) return null
      return getItemTitleRequiredError(this.reimbursement.itemName)
    },
    attachmentRequiredError() {
      if (!this.attachmentValidationAttempted) return null
      return getReimbursementAttachmentRequiredError(this.file)
    },
    formattedFileSize() {
      if (!this.file || typeof this.file.size !== 'number') return ''
      const kb = this.file.size / 1024
      if (kb < 1024) return `${kb.toFixed(1)} KB`
      return `${(kb / 1024).toFixed(2)} MB`
    },
    fileIcon() {
      if (!this.file) return 'FileIcon'
      const name = (this.file.name || '').toLowerCase()
      if (/\.(jpg|jpeg|png|gif|webp)$/.test(name)) return 'ImageIcon'
      if (/\.(pdf)$/.test(name)) return 'FileTextIcon'
      if (/\.(xls|xlsx|csv)$/.test(name)) return 'GridIcon'
      return 'FileIcon'
    },
    amountDisplay() {
      const amt = this.reimbursement.amount
      if (amt == null || amt === '') return '—'
      const num = Number(amt)
      if (Number.isNaN(num)) return '—'
      return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    reviewSummary() {
      const r = this.reimbursement
      return [
        { key: 'submittedBy', label: 'Submitted by', value: this.actorSummaryLabel },
        { key: 'employee', label: 'Employee', value: this.employeeSummaryLabel },
        ...(this.canApplyOnBehalf
          ? [{ key: 'onBehalf', label: 'Apply on behalf of', value: this.onBehalfSummaryLabel }]
          : []),
        { key: 'item', label: 'Item / Title', value: r.itemName || '—' },
        { key: 'account', label: 'Account / Client', value: r.accountName || '—' },
        { key: 'vertical', label: 'Vertical', value: r.vertical || '—' },
        { key: 'category', label: 'Category', value: r.category || '—' },
        { key: 'subCategory', label: 'Sub-Category', value: r.subCategory || '—' },
        { key: 'location', label: 'Location', value: r.location || '—' },
        { key: 'paymentMode', label: 'Payment Mode', value: r.paymentMode || '—' },
        { key: 'expenseDate', label: 'Expense Date', value: r.expenseDate || '—' },
        { key: 'invoiceNo', label: 'Invoice No', value: r.invoiceNo || '—' },
        { key: 'gst', label: 'GST Invoice', value: r.isGSTInvoice ? (r.gstNumber || 'Yes') : 'No' },
        { key: 'comment', label: 'Description', value: r.comment || '—' },
      ]
    },
    formCompletionPercent() {
      let filled = 0
      let total = 0
      REQUIRED_FIELDS.forEach(fieldKey => {
        if (fieldKey === 'subCategory' && !this.isSubCategoryVisible) return
        total += 1
        if (fieldKey === 'file') {
          if (this.file) filled += 1
          return
        }
        const val = this.reimbursement[fieldKey]
        if (val != null && String(val).trim() !== '') filled += 1
      })
      if (this.reimbursement.isGSTInvoice) {
        total += 1
        if (this.reimbursement.gstNumber) filled += 1
      }
      if (this.showTDSPrompt && this.reimbursement.isTDSDeduction) {
        total += 1
        if (this.reimbursement.tdsAmount != null && String(this.reimbursement.tdsAmount).trim() !== '') filled += 1
      }
      if (total === 0) return 0
      return Math.min(100, Math.round((filled / total) * 100))
    },
    actorSummaryLabel() {
      return (this.selfAddedBy && this.selfAddedBy.title) || this.name || '—'
    },
    employeeSummaryLabel() {
      const beneficiary = this.resolveBeneficiary()
      return (beneficiary && beneficiary.title) || this.actorSummaryLabel
    },
    onBehalfSummaryLabel() {
      const selected = this.appliedForSnapshot
        || this.normalizeSelectedEmployee(this.appliedForEmployee)
      if (!this.canApplyOnBehalf || !selected) return 'Myself'
      return selected.title || 'Myself'
    },
  },
  watch: {
    'reimbursement.category'(newVal, oldVal) {
      if (oldVal != null && newVal !== oldVal) {
        this.reimbursement.subCategory = null
      }
    },
    'reimbursement.isGSTInvoice'(newVal) {
      if (!newVal) {
        this.reimbursement.gstNumber = null
      }
    },
  },
  created() {
    const userData = JSON.parse(localStorage.getItem('userData')) || {}
    this.empId = userData.emp_id
    this.name = `${userData.first_name || ''} ${userData.last_name || ''}`.trim()
    this.selfAddedBy = toAddedBySnapshot(userData) || {
      title: this.name,
      empId: this.empId,
      email: userData.email,
    }
    this.reimbursement.addedBy = this.selfAddedBy
    if (this.canApplyOnBehalf) this.ensureOnBehalfEmployees()
  },
  methods: {
    isFieldRequired,
    formatApiError(res) {
      return formatReimbursementApiError(
        res,
        'Failed to raise reimbursement. Please try again or contact Admin.',
      )
    },
    ensureClients() {
      if (this.configLoaded.clients || this.configLoading.clients) return
      this.configLoading.clients = true
      clientService.getClients().then(res => {
        this.liveAccountsList = res.data || []
        this.configLoaded.clients = true
      }).finally(() => {
        this.configLoading.clients = false
      })
    },
    ensureCategories() {
      if (this.configLoaded.categories || this.configLoading.categories) return
      this.configLoading.categories = true
      AdminConfigService.getConfigExpense().then(res => {
        if (res.status === 'OK') {
          this.liveCategoryList = res.data.category || []
          this.liveSubCategoryList = extractExpenseSubCategoryRows(res.data)
          this.livePaymentModeList = res.data.payment_mode || []
        }
        this.configLoaded.categories = true
      }).finally(() => {
        this.configLoading.categories = false
      })
    },
    ensureLocations() {
      if (this.configLoaded.locations || this.configLoading.locations) return
      this.configLoading.locations = true
      locationService.getAll().then(res => {
        this.liveLocationList = res.data || []
        this.configLoaded.locations = true
      }).finally(() => {
        this.configLoading.locations = false
      })
    },
    ensureGstNumbers() {
      if (this.configLoaded.gst || this.configLoading.gst) return
      this.configLoading.gst = true
      invoiceConfigService.getSattvaCompanyDetails().then(res => {
        if (res && res.status === 'OK' && res.data != null) {
          const rows = Array.isArray(res.data) ? res.data : [res.data]
          const gstins = [...new Set(
            rows
              .map(row => (row && (row.gstin || row.gst || row.gst_no || '')).toString().trim().toUpperCase())
              .filter(Boolean),
          )]
          this.liveGstNumberList = gstins.map(title => ({ title }))
        }
        this.configLoaded.gst = true
      }).catch(() => {
        this.configLoaded.gst = true
      }).finally(() => {
        this.configLoading.gst = false
      })
    },
    isActiveEmployee(user) {
      const status = user.status || user.employee_status || user.is_active
      return status === 'Active'
        || status === 'active'
        || status === true
        || status === 'ACTIVE'
        || (status === undefined && (user.emp_id || user.empId))
    },
    ensureOnBehalfEmployees() {
      if (!this.canApplyOnBehalf || this.onBehalfLoaded || this.onBehalfLoading) return
      this.onBehalfLoading = true
      const request = canApplyReimbursementOnBehalfAll(this.$can.bind(this))
        ? userService.getAllUsers()
        : userService.getAllTeamMembersList(this.empId)
      request.then(res => {
        const rows = (res && res.status === 'OK' && res.data)
          ? res.data
          : (res && Array.isArray(res.data) ? res.data : [])
        this.onBehalfEmployees = (Array.isArray(rows) ? rows : []).filter(this.isActiveEmployee)
        this.onBehalfLoaded = true
      }).catch(() => {
        this.onBehalfEmployees = []
      }).finally(() => {
        this.onBehalfLoading = false
      })
    },
    resolveActor() {
      return this.selfAddedBy || {
        title: this.name,
        empId: this.empId,
        email: null,
      }
    },
    normalizeSelectedEmployee(value) {
      if (value == null || value === '') return null
      if (typeof value === 'object') {
        const empId = value.empId ?? value.emp_id ?? value.employee_id ?? null
        if (empId == null || empId === '') return null
        const title = value.title || value.name || value.employee_name || ''
        if (this.empId != null && String(empId) === String(this.empId)) return null
        return {
          title,
          empId,
          email: value.email || null,
        }
      }
      const selected = this.onBehalfEmployeeOptions.find(
        opt => String(opt.empId) === String(value),
      )
      if (selected && selected.empId != null) {
        return {
          title: selected.title,
          empId: selected.empId,
          email: selected.email || null,
        }
      }
      return null
    },
    onAppliedForChange(value) {
      this.appliedForSnapshot = this.normalizeSelectedEmployee(value)
    },
    resolveBeneficiary() {
      if (this.canApplyOnBehalf) {
        const selected = this.appliedForSnapshot
          || this.normalizeSelectedEmployee(this.appliedForEmployee)
        if (selected && selected.empId != null) {
          return selected
        }
      }
      return this.resolveActor()
    },
    resolveAddedBy() {
      const actor = this.resolveActor()
      const beneficiary = this.resolveBeneficiary()
      const isOnBehalf = beneficiary
        && beneficiary.empId != null
        && actor
        && actor.empId != null
        && String(beneficiary.empId) !== String(actor.empId)
      const addedBy = {
        title: actor.title,
        empId: actor.empId,
        email: actor.email || null,
        submitted_by: actor,
        raised_by: actor,
        employee_id: beneficiary.empId,
        employee_name: beneficiary.title,
        employee_email: beneficiary.email || null,
      }
      if (isOnBehalf) {
        addedBy.on_behalf_of = beneficiary.title
        addedBy.applied_for = beneficiary.empId
      }
      return addedBy
    },
    validateItemTitle() {
      this.itemTitleValidationAttempted = true
      return getItemTitleRequiredError(this.reimbursement.itemName)
    },
    triggerFileInput() {
      this.$refs.fileInput?.click()
    },
    onFileSelected(event) {
      const selected = event.target.files?.[0]
      if (selected) this.file = selected
      event.target.value = ''
    },
    onFileDrop(event) {
      this.dragOver = false
      const dropped = event.dataTransfer?.files?.[0]
      if (dropped) this.file = dropped
    },
    clearFile() {
      this.file = null
    },
    openReviewModal() {
      this.submitError = null
      this.attachmentValidationAttempted = true
      const itemTitleError = this.validateItemTitle()
      this.$refs.reimbursementRules.validate().then(success => {
        if (itemTitleError) {
          this.submitError = itemTitleError
          this.scrollToError()
          return
        }
        if (!success) {
          this.submitError = 'Please fill in all mandatory fields highlighted above before submitting.'
          this.scrollToError()
          return
        }
        const missingAttachment = getReimbursementAttachmentRequiredError(this.file)
        if (missingAttachment) {
          this.submitError = missingAttachment
          this.scrollToError()
          return
        }
        const oversized = getReimbursementFileSizeError(this.file)
        if (oversized) {
          this.submitError = oversized
          return
        }
        this.showReviewModal = true
      })
    },
    confirmSubmit() {
      this.submitForm()
    },
    scrollToError() {
      this.$nextTick(() => {
        const el = this.$el.querySelector('.text-danger:not(:empty), .is-invalid, .rr-upload-zone--error')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },
    submitForm() {
      this.submitError = null
      this.attachmentValidationAttempted = true
      const itemTitleError = this.validateItemTitle()
      this.$refs.reimbursementRules.validate().then(success => {
        if (itemTitleError) {
          this.submitError = itemTitleError
          this.showReviewModal = false
          this.scrollToError()
          return
        }
        if (!success) {
          this.submitError = 'Please fill in all mandatory fields highlighted above before submitting.'
          this.showReviewModal = false
          this.scrollToError()
          return
        }
        const missingAttachment = getReimbursementAttachmentRequiredError(this.file)
        if (missingAttachment) {
          this.submitError = missingAttachment
          this.showReviewModal = false
          this.scrollToError()
          return
        }
        const oversized = getReimbursementFileSizeError(this.file)
        if (oversized) {
          this.submitError = oversized
          this.showReviewModal = false
          return
        }

        this.submitting = true
        const formData = new FormData()
        const item = String(this.reimbursement.itemName || '').trim()
        const actor = this.resolveActor()
        const beneficiary = this.resolveBeneficiary()
        const addedBy = this.resolveAddedBy()
        this.reimbursement.addedBy = addedBy
        formData.append('item', item)
        formData.append('account', this.reimbursement.accountName)
        if (SEND_BUSINESS_UNIT) {
          const bu = (BUSINESS_UNIT_DEFAULT || '').trim()
          if (bu) formData.append('business_unit', bu)
        }
        formData.append('vertical', this.reimbursement.vertical || '')
        formData.append('invoice_no', this.reimbursement.invoiceNo || '')
        formData.append('category', this.reimbursement.category || '')
        formData.append('sub_category', this.reimbursement.subCategory || '')
        formData.append('expense_date', this.reimbursement.expenseDate || '')
        formData.append('amount', Number(this.reimbursement.amount))
        formData.append('payment_mode', this.reimbursement.paymentMode)
        formData.append('is_gst', String(!!this.reimbursement.isGSTInvoice))
        if (this.reimbursement.isGSTInvoice && this.reimbursement.gstNumber) {
          formData.append('gst_number', this.reimbursement.gstNumber)
        }
        if (SEND_GST_AMOUNT) {
          formData.append('gst_amount', 0)
        }
        formData.append('is_tds', String(!!this.reimbursement.isTDSDeduction))
        formData.append(
          'tds_amount',
          this.reimbursement.isTDSDeduction ? Number(this.reimbursement.tdsAmount) : 0,
        )
        formData.append('location', this.reimbursement.location)
        formData.append('comment', this.reimbursement.comment)
        formData.append('added_by', JSON.stringify(addedBy))
        formData.append('submitted_by', JSON.stringify(actor))
        formData.append('raised_by', JSON.stringify(actor))
        if (beneficiary && beneficiary.empId != null && beneficiary.empId !== '') {
          formData.append('employee_id', beneficiary.empId)
          formData.append('applied_for', beneficiary.empId)
        }
        if (beneficiary && beneficiary.title) {
          formData.append('employee_name', beneficiary.title)
        }
        if (
          actor
          && beneficiary
          && actor.empId != null
          && beneficiary.empId != null
          && String(beneficiary.empId) !== String(actor.empId)
          && beneficiary.title
        ) {
          formData.append('on_behalf_of', beneficiary.title)
        }
        formData.append('is_auto_approved', 'false')

        // Attachment is mandatory — always append after validation above.
        formData.append(REIMBURSEMENT_FILE_FIELD, this.file)

        reimbursementService.createReimbursement(formData).then(res => {
          this.submitting = false
          if (res.status === 'OK') {
            this.showReviewModal = false
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Reimbursement raised successfully!',
                icon: 'CheckIcon',
                variant: 'success',
                timeout: 5000,
              },
            })
            this.$router.push({ name: 'apps-reimbursement' })
            const created = (res.data && res.data.data && typeof res.data.data === 'object')
              ? res.data.data
              : res.data
            rememberReimbursementOnBehalf(created && created.id, beneficiary)
            reimbursementService.notifyReimbursementEmails('submitted', res.data, {
              fallback: {
                item: String(this.reimbursement.itemName || '').trim(),
                account: this.reimbursement.accountName,
                sub_category: this.reimbursement.subCategory,
                category: this.reimbursement.category,
                amount: this.reimbursement.amount,
                expense_date: this.reimbursement.expenseDate,
                payment_mode: this.reimbursement.paymentMode,
                location: this.reimbursement.location,
                comment: this.reimbursement.comment,
                vertical: this.reimbursement.vertical,
                added_by: this.reimbursement.addedBy,
                file_path: (res.data && (res.data.file_path || res.data.filePath))
                  || (res.data && res.data.data && (res.data.data.file_path || res.data.data.filePath))
                  || null,
              },
            })
              .then(({ failures }) => {
                if (failures?.length) {
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: 'Notification email could not be sent.',
                      icon: 'AlertTriangleIcon',
                      variant: 'warning',
                      timeout: 4000,
                    },
                  })
                }
              })
              .catch(err => {
                console.warn('[reimbursement] email notification error:', err)
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: 'Notification email could not be sent.',
                    icon: 'AlertTriangleIcon',
                    variant: 'warning',
                    timeout: 4000,
                  },
                })
              })
          } else if ((res.status === 401) || (res.status === 403)) {
            window.sessionStorage.setItem('lastPage', window.location.href.split('/#')[1])
            localStorage.clear()
            this.$router.push({ name: 'auth-login' })
          } else {
            const errorMessage = this.formatApiError(res)
            this.submitError = errorMessage
            this.showReviewModal = false
            this.$toast({
              component: ToastificationContent,
              props: {
                title: errorMessage,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                timeout: 8000,
              },
            })
          }
        }).catch((err) => {
          this.submitting = false
          this.showReviewModal = false
          const errorMessage = this.formatApiError(err?.response?.data)
          this.submitError = errorMessage
          this.$toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              timeout: 8000,
            },
          })
        })
      })
    },
    reset() {
      this.reimbursement = {
        itemName: null,
        accountName: null,
        vertical: null,
        invoiceNo: null,
        category: null,
        subCategory: null,
        expenseDate: null,
        paymentMode: null,
        isGSTInvoice: false,
        isTDSDeduction: false,
        amount: null,
        gstNumber: null,
        tdsAmount: null,
        location: null,
        comment: null,
        addedBy: this.selfAddedBy,
      }
      this.appliedForEmployee = null
      this.appliedForSnapshot = null
      this.file = null
      this.submitError = null
      this.showReviewModal = false
      this.attachmentValidationAttempted = false
      this.itemTitleValidationAttempted = false
      this.$nextTick(() => {
        this.$refs.reimbursementRules.reset()
      })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/quill.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style lang="scss" scoped>
$rr-primary: #00AC99;
$rr-accent: #7367f0;
$rr-border: #ebe9f1;
$rr-text-muted: #6e6b7b;
$rr-text-heading: #5e5873;

.reimbursement-add-page {
  margin-top: -0.85rem;
  padding-bottom: 5rem;

  @media (min-width: 992px) {
    padding-bottom: 1rem;
  }
}

// Intro card
.rr-intro-card {
  border: 1px solid rgba($rr-primary, 0.15);
  background: linear-gradient(135deg, rgba($rr-primary, 0.04) 0%, rgba($rr-accent, 0.03) 100%);
}

.rr-intro-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba($rr-primary, 0.12);
  color: $rr-primary;
  flex-shrink: 0;
}

.rr-intro-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: $rr-text-heading;
}

.rr-intro-text {
  font-size: 0.875rem;
  color: $rr-text-muted;
  line-height: 1.5;
}

.rr-back-link {
  display: inline-flex;
  align-items: center;
  font-size: 0.8125rem;
  color: $rr-accent;
  white-space: nowrap;

  &:hover {
    color: darken($rr-accent, 8%);
    text-decoration: none;
  }
}

// Section cards
.rr-section-card {
  border: 1px solid $rr-border;
  box-shadow: 0 2px 8px rgba(34, 41, 47, 0.04);
}

.rr-section-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 0.85rem;
}

.rr-section-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;

  &.variant-primary {
    background-color: rgba($rr-primary, 0.12);
    color: $rr-primary;
  }

  &.variant-muted {
    background-color: rgba(110, 107, 123, 0.1);
    color: $rr-text-muted;
  }

  &.variant-accent {
    background-color: rgba($rr-accent, 0.12);
    color: $rr-accent;
  }
}

.rr-section-title {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $rr-text-heading;
}

.rr-section-rule {
  border-bottom: 1px dashed $rr-border;
  min-width: 24px;
  height: 1px;
  align-self: center;
}

// File upload
.rr-upload-hint {
  font-size: 0.8125rem;
  color: $rr-text-muted;
}

.rr-upload-zone {
  border: 2px dashed rgba($rr-accent, 0.35);
  border-radius: 8px;
  padding: 1.25rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  background: rgba($rr-accent, 0.02);

  &:hover {
    border-color: $rr-accent;
    background: rgba($rr-accent, 0.05);
  }

  &--has-file {
    padding: 0.75rem;
    border-style: solid;
    border-color: rgba($rr-primary, 0.3);
    background: rgba($rr-primary, 0.03);
  }

  &--error {
    border-color: #ea5455;
    background: rgba(234, 84, 85, 0.04);
  }

  &--dragover {
    border-color: $rr-accent;
    background: rgba($rr-accent, 0.08);
  }
}

.rr-upload-zone-icon {
  color: $rr-accent;
  margin-bottom: 0.5rem;
}

.rr-upload-zone-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: $rr-text-heading;
}

.rr-upload-zone-sub {
  font-size: 0.75rem;
}

.rr-file-preview {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.85rem;
  border: 1px solid $rr-border;
  border-radius: 8px;
  background: #fff;
}

.rr-file-preview-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba($rr-primary, 0.1);
  color: $rr-primary;
  flex-shrink: 0;
}

.rr-file-preview-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: $rr-text-heading;
  word-break: break-all;
}

.rr-file-preview-size {
  font-size: 0.75rem;
  color: $rr-text-muted;
}

// Summary sidebar
.rr-sidebar-sticky {
  position: sticky;
  top: 5.5rem;
}

.rr-summary-card {
  border: 1px solid rgba($rr-accent, 0.15);
  background: linear-gradient(180deg, rgba($rr-accent, 0.03) 0%, #fff 40%);

  .rr-section-title {
    font-size: 0.875rem;
  }
}

.rr-progress-block {
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  background: rgba($rr-primary, 0.04);
}

.rr-summary-meta-label {
  font-size: 0.875rem;
  color: $rr-text-heading;
}

.rr-summary-meta-value {
  font-size: 0.9375rem;
}

.rr-amount-highlight,
.rr-review-amount {
  padding: 0.75rem;
  border-radius: 8px;
  background: rgba($rr-primary, 0.08);
  text-align: center;
}

.rr-amount-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: $rr-primary;
  letter-spacing: -0.01em;
}

::v-deep .rr-summary-list {
  list-style: none;
  padding: 0;
  margin: 0;

  &--dense .rr-summary-item {
    padding: 0.35rem 0;
  }
}

::v-deep .rr-summary-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.45rem 0;
  border-bottom: 1px solid rgba($rr-border, 0.7);
  font-size: 0.9375rem;

  &:last-child {
    border-bottom: none;
  }
}

::v-deep .rr-summary-label {
  color: $rr-text-heading;
  flex-shrink: 0;
}

::v-deep .rr-summary-value {
  color: $rr-text-heading;
  font-weight: 600;
  text-align: right;
  word-break: break-word;
}

::v-deep .rr-summary-empty {
  font-size: 0.875rem;
  font-style: italic;
}

.rr-approval-hint {
  display: flex;
  align-items: flex-start;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  background: rgba($rr-accent, 0.05);
  border: 1px dashed rgba($rr-accent, 0.2);
}

.rr-approval-hint-text {
  font-size: 0.875rem;
  line-height: 1.45;
  color: $rr-text-heading;
}

// Form controls
::v-deep .v-select,
::v-deep .form-control {
  max-width: 100%;
}

// Actions
.rr-form-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.5rem 0 1rem;

  .btn {
    min-height: 44px;
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  }
}

.rr-submit-btn {
  background: $rr-primary !important;
  border-color: $rr-primary !important;

  &:hover:not(:disabled) {
    background: darken($rr-primary, 6%) !important;
    border-color: darken($rr-primary, 6%) !important;
  }
}

.rr-error-alert {
  border-radius: 8px;
}

// Mobile sticky action bar
.rr-mobile-action-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1030;
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #fff;
  border-top: 1px solid $rr-border;
  box-shadow: 0 -4px 16px rgba(34, 41, 47, 0.08);
}

.rr-mobile-reset-btn {
  flex: 0 0 auto;
  min-height: 44px;
}

.rr-mobile-submit-btn {
  flex: 1 1 auto;
  min-height: 44px;
  background: $rr-primary !important;
  border-color: $rr-primary !important;
}
</style>
